/**
 * Local storage and session management utilities
 */

export type StorageType = 'localStorage' | 'sessionStorage'

/**
 * Set flag for first input completion
 */
export function setFirstInputDone(flag: boolean = true): void {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem('firstInputDone', flag.toString())
  }
}

/**
 * Check if first input is done
 */
export function isFirstInputDone(): boolean {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage.getItem('firstInputDone') === 'true'
  }
  return false
}

/**
 * Set order detail in storage
 */
export function setOrderDetail(value: any): void {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem('orderDetail', JSON.stringify(value))
  }
}

/**
 * Remove order detail from storage
 */
export function removeOrderDetail(): void {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem('orderDetail')
  }
}

/**
 * Get order detail from storage
 */
export function getOrderDetail(): any {
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem('orderDetail')
    try {
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }
  return null
}

/**
 * Generic storage setter with expiry
 */
export function setStorageItem(
  key: string,
  value: any,
  storageType: StorageType = 'localStorage',
  expiryInHours?: number
): void {
  if (typeof window === 'undefined') {
    return
  }

  const storage = window[storageType]
  if (!storage) {
    return
  }

  const item = {
    value,
    timestamp: Date.now(),
    expiry: expiryInHours ? Date.now() + expiryInHours * 60 * 60 * 1000 : undefined,
  }

  try {
    storage.setItem(key, JSON.stringify(item))
  } catch (error) {}
}

/**
 * Generic storage getter with expiry check
 */
export function getStorageItem(key: string, storageType: StorageType = 'localStorage'): any {
  if (typeof window === 'undefined') {
    return null
  }

  const storage = window[storageType]
  if (!storage) {
    return null
  }

  try {
    const stored = storage.getItem(key)
    if (!stored) {
      return null
    }

    const item = JSON.parse(stored)

    // Check expiry
    if (item.expiry && Date.now() > item.expiry) {
      storage.removeItem(key)
      return null
    }

    return item.value
  } catch {
    return null
  }
}

/**
 * Remove storage item
 */
export function removeStorageItem(key: string, storageType: StorageType = 'localStorage'): void {
  if (typeof window !== 'undefined') {
    const storage = window[storageType]
    storage?.removeItem(key)
  }
}

/**
 * Clear all expired items from storage
 */
export function clearExpiredStorage(storageType: StorageType = 'localStorage'): void {
  if (typeof window === 'undefined') {
    return
  }

  const storage = window[storageType]
  if (!storage) {
    return
  }

  const keys = Object.keys(storage)
  const now = Date.now()

  keys.forEach(key => {
    try {
      const stored = storage.getItem(key)
      if (stored) {
        const item = JSON.parse(stored)
        if (item.expiry && now > item.expiry) {
          storage.removeItem(key)
        }
      }
    } catch {
      // Ignore malformed items
    }
  })
}

/**
 * Get storage size in bytes
 */
export function getStorageSize(storageType: StorageType = 'localStorage'): number {
  if (typeof window === 'undefined') {
    return 0
  }

  const storage = window[storageType]
  if (!storage) {
    return 0
  }

  let size = 0
  for (const key in storage) {
    if (storage.hasOwnProperty(key)) {
      size += storage[key].length + key.length
    }
  }

  return size
}

/**
 * Check if storage is available
 */
export function isStorageAvailable(storageType: StorageType = 'localStorage'): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const storage = window[storageType]
    const testKey = '__storage_test__'
    storage.setItem(testKey, 'test')
    storage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

// Generic storage interface

export interface StorageValue<T> {
  value: T
  timestamp: number
  expiryMs?: number
}

/**
 * Set item in storage with expiry support (generic version)
 */
export function setStorageItemWithExpiry<T>(
  key: string,
  value: T,
  expiryMs?: number,
  storageType: StorageType = 'localStorage'
): void {
  if (!isStorageAvailable(storageType)) {
    return
  }

  const storageValue: StorageValue<T> = {
    value,
    timestamp: Date.now(),
    ...(expiryMs && { expiryMs }),
  }

  try {
    window[storageType].setItem(key, JSON.stringify(storageValue))
  } catch (error) {}
}

/**
 * Get item from storage with expiry check (generic version)
 */
export function getStorageItemWithExpiry<T>(key: string, storageType: StorageType = 'localStorage'): T | null {
  if (!isStorageAvailable(storageType)) {
    return null
  }

  try {
    const stored = window[storageType].getItem(key)
    if (!stored) {
      return null
    }

    const storageValue: StorageValue<T> = JSON.parse(stored)

    // Check expiry
    if (storageValue.expiryMs) {
      const isExpired = Date.now() - storageValue.timestamp > storageValue.expiryMs
      if (isExpired) {
        window[storageType].removeItem(key)
        return null
      }
    }

    return storageValue.value
  } catch (error) {
    return null
  }
}

/**
 * Remove item from storage (generic version)
 */
export function removeStorageItemGeneric(key: string, storageType: StorageType = 'localStorage'): void {
  if (!isStorageAvailable(storageType)) {
    return
  }

  try {
    window[storageType].removeItem(key)
  } catch (error) {}
}

/**
 * Clear all storage
 */
export function clearStorage(storageType: StorageType = 'localStorage'): void {
  if (!isStorageAvailable(storageType)) {
    return
  }

  try {
    window[storageType].clear()
  } catch (error) {}
}
